import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getAggregatedAddress, formatTFN } from '../../common/common';
import { useServiceablitityData, useSessionData } from '../../hooks/order';
import { saveInternetPlan, setSelectedBundle } from '../../store/action/OrderAction';
import { routesConfig, WHI_TFN } from '../../util/util';
import EarthlinkPerks from './Products/EarthlinkPerks';
import { getWhiPerkItems } from './Products/whiPerkItems';
import StaticResultsView from './Products/StaticResultsView';

/**
 * ELNKWEB-3210 - results view for addresses where nothing is serviceable.
 *
 * Replaces the previous interstitial, which auto-redirected to highspeedoptions.com after
 * five seconds and listed competitor logos. Both are gone: the requirements doc forbids
 * linking out to any third-party or reseller site from this page.
 *
 * The cards themselves live in StaticResultsView, shared with the 5G-only state, because the
 * doc requires the two states to render identically.
 */
const NonServiceable = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { normalizedAddress } = useServiceablitityData();
  const { invocaTfn } = useSessionData();

  // Single call-tracking number across the whole page - nav, every card, and the promo
  // button - per the requirements doc. WHI_TFN is the number 5G traffic already routes to,
  // so call attribution stays with the 5G queue rather than the fiber one. Invoca takes
  // precedence when it has leased a pool number, the same way every other CTA in the app
  // resolves it, so the header and these CTAs cannot disagree.
  const phoneNumber = invocaTfn || formatTFN(WHI_TFN);

  // Serviceability may never have completed (the "address not seen" path arrives here with
  // no normalized address), so the address line is only rendered when we actually have one.
  const address = normalizedAddress ? getAggregatedAddress(normalizedAddress) : '';

  const onResetAddress = useCallback(() => {
    navigate(routesConfig.home);
    dispatch(saveInternetPlan({}));
    dispatch(setSelectedBundle(null));
  }, [navigate, dispatch]);

  return (
    <div className="non-serviceable-container p-top-5 height-big-screen">
      <StaticResultsView
        phoneNumber={phoneNumber}
        address={address}
        onResetAddress={onResetAddress}
        productCategory="non-serviceable"
      />

      <EarthlinkPerks phoneNumber={phoneNumber} perkItems={getWhiPerkItems(t)} />

      {/* This route does not render Product.js, so unlike the 5G-only state it has to carry
          the WHI disclaimers itself. The ** footnote is load-bearing - the middle card claims
          "No contracts", and this is the qualifier limiting that to Wireless Home Internet. */}
      <section className="desclaimer-section">
        <div className="elink-container">
          <div>
            <p style={{ marginBottom: '20px' }}>{t('products.disclaimerWhi1')}</p>
            <p>{t('products.disclaimerWhi2')}</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NonServiceable;
